import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Alert,
  AlertTitle,
  Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Info } from '@mui/icons-material';
import ImageOverlay from './ImageOverlay';

const ResourceLink = ({ icon, primary, secondary, href, external, onClick, path }) => {
  const navigate = useNavigate();
  const handleClick = onClick || (path ? () => navigate(path) : undefined);

  if (handleClick) {
    return (
      <ListItemButton 
        onClick={handleClick}
        sx={{ 
          py: 1.5,
          px: 2,
          color: '#2e2e2e',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(53, 152, 183, 0.08)',
          }
        }}
      >
        <ListItemIcon sx={{ color: '#3598b7', minWidth: 40 }}>
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={primary}
          secondary={secondary}
          primaryTypographyProps={{
            sx: { color: 'inherit', fontWeight: 500 }
          }}
          secondaryTypographyProps={{
            sx: { color: 'rgba(46, 46, 46, 0.7)' }
          }}
        />
      </ListItemButton>
    );
  }

  return (
    <ListItemButton 
      component="a"
      href={href}
      target={external ? "_blank" : "_self"}
      rel={external ? "noopener noreferrer" : ""}
      sx={{ 
        py: 1.5,
        px: 2,
        color: '#2e2e2e',
        textDecoration: 'none',
        '&:hover': {
          backgroundColor: 'rgba(53, 152, 183, 0.08)',
        }
      }}
    >
      <ListItemIcon sx={{ color: '#3598b7', minWidth: 40 }}>
        {icon}
      </ListItemIcon>
      <ListItemText 
        primary={primary}
        secondary={secondary}
        primaryTypographyProps={{
          sx: { color: 'inherit', fontWeight: 500 }
        }}
        secondaryTypographyProps={{
          sx: { color: 'rgba(46, 46, 46, 0.7)' }
        }}
      />
    </ListItemButton>
  );
};

const ResourceSidebar = ({ alert, quickResources }) => {
  const [imageOverlayOpen, setImageOverlayOpen] = useState(false);
  const [imageOverlayData, setImageOverlayData] = useState(null);

  const handleImageClick = (imageSrc, title) => {
    setImageOverlayData({ imageSrc, title });
    setImageOverlayOpen(true);
  };

  const handleCloseImageOverlay = () => {
    setImageOverlayOpen(false);
    setImageOverlayData(null);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={2}>
        {alert && (
          <Alert 
            severity={alert.severity || "warning"} 
            sx={{ 
              borderRadius: 0,
              boxShadow: 'none'
            }}
          >
            {alert.title && <AlertTitle>{alert.title}</AlertTitle>}
            {alert.message}
          </Alert>
        )}

        {quickResources.map((section, index) => (
          <Card key={index} sx={{ 
            backgroundColor: '#ffffff', 
            borderRadius: 0,
            boxShadow: 'none',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
              <Typography variant="h6" sx={{ color: '#3598b7', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <Info sx={{ mr: 1, fontSize: 20 }} />
                {section.title}
              </Typography>
            </Box>
            <List disablePadding sx={{ flex: 1 }}>
              {section.items.map((item, itemIndex) => {
                const onClick = item.imageSrc 
                  ? () => handleImageClick(item.imageSrc, item.primary)
                  : undefined;

                return (
                  <React.Fragment key={itemIndex}>
                    <ResourceLink 
                      {...item} 
                      onClick={onClick}
                    />
                    {itemIndex < section.items.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          </Card>
        ))}
      </Stack>

      {imageOverlayData && (
        <ImageOverlay
          open={imageOverlayOpen}
          onClose={handleCloseImageOverlay}
          imageSrc={imageOverlayData.imageSrc}
          title={imageOverlayData.title}
        />
      )}
    </Box>
  );
};

export default ResourceSidebar;
